import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../../config';
import { Theme } from '../../ui/theme';
import { MATH_STAGES, MATH_STAGES_BY_NUMBER } from '../../data/mathStages';
import { GameState } from '../../systems/GameState';
import { EconomyEngine } from '../../systems/EconomyEngine';
import { MiniGameRunner } from '../../systems/MiniGameRunner';
import { Button } from '../../ui/Button';
import { ToastManager } from '../../ui/ToastManager';
import { AudioManager } from '../../systems/AudioManager';
import type { MathProblem } from '../../types/index';

const PROBLEMS_PER_STAGE = 10;

export class MathChallengeScene extends Phaser.Scene {
  private toast!: ToastManager;
  private screenContainer: Phaser.GameObjects.Container | null = null;

  constructor() { super('MathChallengeScene'); }

  create(): void {
    AudioManager.setScene(this);
    AudioManager.playBgm('math');
    this.cameras.main.setBackgroundColor('#e8f0ff');
    this.toast = new ToastManager(this);

    this.add.text(GAME_WIDTH/2, 72, '🔢 수학 챌린지', { fontFamily: Theme.font.family, fontSize: '32px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);

    this.showStageSelect();
    new Button(this, 100, GAME_HEIGHT-50, '← 종료', () => this.scene.start('MiniGameHubScene'), { width: 120, height: 48, style: 'secondary' });
  }

  private showStageSelect(): void {
    if (this.screenContainer) this.screenContainer.destroy();
    this.screenContainer = this.add.container(0,0);

    const cleared = new Set(GameState.data.mathProgress.filter(p => p.clearedAt).map(p => p.stage));
    const maxUnlocked = (Math.max(0, ...cleared) + 1);

    this.add.text(GAME_WIDTH/2, 125, '단계를 선택하세요', { fontFamily: Theme.font.family, fontSize: '18px', fontStyle: '700', color: Theme.hex.textMid }).setOrigin(0.5);

    const startX = 200;
    const startY = 180;
    const cols = 10;
    const cellW = 100, cellH = 80;

    MATH_STAGES.forEach((s, i) => {
      const x = startX + (i % cols) * cellW;
      const y = startY + Math.floor(i / cols) * cellH;
      const isCleared = cleared.has(s.stage);
      const isLocked = s.stage > maxUnlocked;
      const color = isCleared ? 0xffd95c : isLocked ? 0x999999 : 0xffffff;
      const bg = this.add.rectangle(x, y, cellW - 8, cellH - 8, color);
      bg.setStrokeStyle(2, 0x3a2540);
      const num = this.add.text(x, y - 10, `${s.stage}`, { fontFamily: Theme.font.family, fontSize: '20px', fontStyle: '900', color: isLocked ? '#fff' : Theme.hex.text }).setOrigin(0.5);
      const label = this.add.text(x, y + 18, s.semester, { fontFamily: Theme.font.family, fontSize: '11px', fontStyle: '700', color: isLocked ? '#fff' : Theme.hex.textMid }).setOrigin(0.5);
      this.screenContainer!.add([bg, num, label]);
      if (!isLocked) {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => this.startStage(s.stage));
      }
    });
  }

  private startStage(stageNum: number): void {
    const stage = MATH_STAGES_BY_NUMBER[stageNum];
    if (this.screenContainer) this.screenContainer.destroy();
    this.screenContainer = this.add.container(0,0);

    const problems: MathProblem[] = Array.from({length: PROBLEMS_PER_STAGE}, () => stage.generator());
    let currentIdx = 0;
    let correctCount = 0;
    let timeLeft = stage.timeLimit ?? 0;
    let timerEvent: Phaser.Time.TimerEvent | null = null;

    const stageTitle = this.add.text(GAME_WIDTH/2, 125, `단계 ${stage.stage}: ${stage.topic}`, { fontFamily: Theme.font.family, fontSize: '22px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);
    const progressText = this.add.text(GAME_WIDTH/2, 165, '', { fontFamily: Theme.font.family, fontSize: '16px', color: Theme.hex.textMid }).setOrigin(0.5);
    const questionText = this.add.text(GAME_WIDTH/2, 320, '', { fontFamily: Theme.font.family, fontSize: '64px', fontStyle: '900', color: Theme.hex.text }).setOrigin(0.5);
    const timerText = this.add.text(GAME_WIDTH-100, 32, '', { fontFamily: Theme.font.family, fontSize: '22px', fontStyle: '900', color: Theme.hex.pinkDark }).setOrigin(0.5);

    // DOM input
    const inputEl = document.createElement('input');
    inputEl.type = 'number';
    inputEl.style.fontFamily = 'Pretendard Variable, sans-serif';
    inputEl.style.fontSize = '48px';
    inputEl.style.fontWeight = '900';
    inputEl.style.padding = '20px 30px';
    inputEl.style.border = '4px solid #ff6fa1';
    inputEl.style.borderRadius = '16px';
    inputEl.style.textAlign = 'center';
    inputEl.style.color = '#3a2540';
    inputEl.style.width = '300px';
    inputEl.style.outline = 'none';
    const domEl = this.add.dom(GAME_WIDTH/2, 480, inputEl);

    this.screenContainer.add([stageTitle, progressText, questionText, timerText, domEl]);

    const renderProblem = () => {
      const p = problems[currentIdx];
      questionText.setText(p.question);
      progressText.setText(`${currentIdx + 1} / ${PROBLEMS_PER_STAGE} (정답 ${correctCount})`);
      inputEl.value = '';
      inputEl.focus();
    };

    const submit = () => {
      const userAnswer = parseInt(inputEl.value, 10);
      const correct = userAnswer === problems[currentIdx].answer;
      if (correct) {
        correctCount++;
        AudioManager.playSfx('correct');
        this.toast.show('✓ 정답!', 'gold');
        // Green flash
        const flash = this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT/2, GAME_WIDTH, GAME_HEIGHT, 0x44ee88, 0.25).setDepth(9999);
        this.tweens.add({ targets: flash, alpha: 0, duration: 350, onComplete: () => flash.destroy() });
      } else {
        AudioManager.playSfx('wrong');
        this.toast.show(`❌ 정답: ${problems[currentIdx].answer}`);
        // Red flash
        const flash = this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT/2, GAME_WIDTH, GAME_HEIGHT, 0xe54e87, 0.25).setDepth(9999);
        this.tweens.add({ targets: flash, alpha: 0, duration: 350, onComplete: () => flash.destroy() });
      }
      currentIdx++;
      if (currentIdx >= PROBLEMS_PER_STAGE) endStage();
      else renderProblem();
    };

    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') submit();
    });

    new Button(this, GAME_WIDTH - 200, 580, '제출 ✨', submit, { width: 200, height: 60, style: 'gold' });

    const endStage = () => {
      if (timerEvent) timerEvent.remove();
      const passed = correctCount >= stage.passThreshold;
      if (passed) {
        // 진행도 갱신
        const existing = GameState.data.mathProgress.find(p => p.stage === stage.stage);
        if (existing) {
          existing.bestScore = Math.max(existing.bestScore, correctCount);
          if (!existing.clearedAt) existing.clearedAt = Date.now();
        } else {
          GameState.data.mathProgress.push({ stage: stage.stage, bestScore: correctCount, clearedAt: Date.now() });
        }
        // 보상
        EconomyEngine.grant({ stars: stage.reward.stars, coins: stage.reward.coins });
        if (stage.reward.outfitId && !GameState.data.ownedOutfits.some(o => o.outfitId === stage.reward.outfitId)) {
          GameState.data.ownedOutfits.push({ outfitId: stage.reward.outfitId, acquiredAt: Date.now() });
        }
        // 미니게임 best score (수학은 단계*10 단위)
        MiniGameRunner.reportResult('math', stage.stage * 10);
        this.toast.show(`🎉 클리어! ${correctCount}/${PROBLEMS_PER_STAGE} ⭐+${stage.reward.stars}, 💰+${stage.reward.coins}`, 'gold');
      } else {
        this.toast.show(`${correctCount}/${PROBLEMS_PER_STAGE} - 다시 도전!`);
      }
      GameState.persist();
      this.time.delayedCall(2000, () => {
        this.cameras.main.fadeOut(200);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.cameras.main.fadeIn(200);
          this.showStageSelect();
        });
      });
    };

    if (stage.timeLimit) {
      timerEvent = this.time.addEvent({ delay: 100, loop: true, callback: () => {
        timeLeft -= 100;
        timerText.setText(`⏱ ${Math.ceil(timeLeft/1000)}s`);
        if (timeLeft <= 0) endStage();
      }});
    }

    renderProblem();
  }
}
